import React, { useEffect, useRef, memo, createElement } from 'react';
interface TradingViewChartProps {
  symbol?: string;
  theme?: 'light' | 'dark';
  interval?: string;
  height?: number | string;
  width?: number | string;
}
export const TradingViewChart = memo(function TradingViewChart({
  symbol = 'BITSTAMP:BTCUSD',
  theme = 'dark',
  interval = 'D',
  height = '100%',
  width = '100%'
}: TradingViewChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    // Clear any existing content
    containerRef.current.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = () => {
      if (typeof window.TradingView !== 'undefined' && containerRef.current) {
        new window.TradingView.widget({
          autosize: true,
          symbol: symbol,
          interval: interval,
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1',
          locale: 'en',
          // Custom color scheme matching your UI
          toolbar_bg: '#1A1A2E',
          enable_publishing: false,
          hide_top_toolbar: true,
          hide_legend: true,
          save_image: false,
          container_id: containerRef.current.id,
          // Background colors
          backgroundColor: '#1A1A2E',
          // Grid colors
          gridColor: 'rgba(255, 255, 255, 0.06)',
          // Disable branding and extra features
          disabled_features: ['header_widget', 'header_symbol_search', 'symbol_search_hot_key', 'header_compare', 'compare_symbol', 'header_undo_redo', 'header_screenshot', 'header_chart_type', 'header_settings', 'header_indicators', 'header_fullscreen_button', 'left_toolbar', 'control_bar', 'timeframes_toolbar', 'display_market_status', 'show_interval_dialog_on_key_press'],
          enabled_features: ['hide_left_toolbar_by_default'],
          // Overrides for matching your color palette
          overrides: {
            // Background colors
            'paneProperties.background': '#1A1A2E',
            'paneProperties.backgroundType': 'solid',
            // Grid
            'paneProperties.vertGridProperties.color': 'rgba(255, 255, 255, 0.06)',
            'paneProperties.horzGridProperties.color': 'rgba(255, 255, 255, 0.06)',
            // Candles
            'mainSeriesProperties.candleStyle.upColor': '#00C87F',
            'mainSeriesProperties.candleStyle.downColor': '#E85D75',
            'mainSeriesProperties.candleStyle.borderUpColor': '#00C87F',
            'mainSeriesProperties.candleStyle.borderDownColor': '#E85D75',
            'mainSeriesProperties.candleStyle.wickUpColor': '#00C87F',
            'mainSeriesProperties.candleStyle.wickDownColor': '#E85D75',
            // Volume
            volumePaneSize: 'medium',
            // Scales
            'scalesProperties.backgroundColor': '#1A1A2E',
            'scalesProperties.lineColor': '#2A2A3E',
            'scalesProperties.textColor': '#8B8B9E',
            // Crosshair
            'crosshairProperties.color': '#7A2EFF',
            // Watermark - remove TradingView branding
            'paneProperties.backgroundGradientStartColor': '#1A1A2E',
            'paneProperties.backgroundGradientEndColor': '#1A1A2E'
          },
          studies_overrides: {
            // Volume colors
            'volume.volume.color.0': '#E85D75',
            'volume.volume.color.1': '#00C87F',
            'volume.volume.transparency': 50
          },
          hide_side_toolbar: true,
          allow_symbol_change: false,
          details: false,
          hotlist: false,
          calendar: false,
          show_popup_button: false,
          support_host: 'https://www.tradingview.com'
        });
      }
    };
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [symbol, theme, interval]);
  return <div ref={containerRef} id={`tradingview_${Math.random().toString(36).substring(7)}`} style={{
    height: typeof height === 'number' ? `${height}px` : height,
    width: typeof width === 'number' ? `${width}px` : width
  }} data-id="element-1793" />;
});
// Extend Window interface for TypeScript
declare global {
  interface Window {
    TradingView: any;
  }
}